import React from 'react';
import styled from 'styled-components';
import { neutral100, blue500, white, neutral400, blue600 } from '@prezi/common-design';
import { Body2 } from '../Typography/Typography';

export type CheckboxProps = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
    display: none;
`;

const Checkmark = styled.svg`
    width: 12px;
    height: 12px;
`;

const StyledCheckbox = styled.div<{
    checked: CheckboxProps['checked'];
    disabled: CheckboxProps['disabled'];
}>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    outline: none;
    background: ${({ checked, disabled }) => (checked ? (disabled ? neutral400 : blue500) : white)};
    border: ${({ checked, disabled }) => `1px solid ${checked ? (disabled ? neutral400 : blue500) : neutral100}`};
    border-radius: 4px;
    transition: all 0.1s;

    :hover {
        border: ${({ checked, disabled }) =>
            `1px solid ${checked ? (disabled ? neutral400 : blue600) : disabled ? neutral100 : neutral400}`};
    }

    :focus {
        box-shadow: 0 0 0 2px ${({ theme }) => theme.bento.colors.primary.weak};
    }

    ${Checkmark} {
        opacity: ${({ checked, disabled }) => (checked ? (disabled ? 0.4 : 1) : 0)};
    }
`;

const CheckboxWrapper = styled.label<{
    checked: CheckboxProps['checked'];
    disabled: CheckboxProps['disabled'];
}>`
    display: flex;
    align-items: center;
    opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};

    &:hover {
        cursor: ${({ disabled }) => (disabled ? 'initial' : 'pointer')};

        ${StyledCheckbox} {
            border: ${({ checked, disabled }) =>
                `1px solid ${checked ? (disabled ? neutral400 : blue600) : disabled ? neutral100 : neutral400}`};
        }
    }
`;

const Label = styled(Body2)`
    padding-left: 8px;
    line-height: 18px;
`;

export const Checkbox: React.FC<CheckboxProps> = ({ checked, onChange, disabled, className, children }) => {
    return (
        <CheckboxWrapper checked={checked} disabled={disabled} className={className}>
            <HiddenCheckbox checked={checked} onChange={onChange} disabled={disabled} />
            <StyledCheckbox checked={checked} disabled={disabled}>
                <Checkmark viewBox="0 0 12 9">
                    <path d="M10.25.2l1.41 1.41L4.6 8.7.35 4.44l1.41-1.41L4.6 5.86 10.25.2z" fill={white} />
                </Checkmark>
            </StyledCheckbox>
            {children && <Label>{children}</Label>}
        </CheckboxWrapper>
    );
};
